/**
 * ROOM CHOICE AT SELL PRICES — a normal quote the customer configures room by room (18 Oct 2026).
 *
 * Jacky, for Minto (P-1026-10296): *"give him proposal link to pick grouped area section or individual room to select
 * product while in green suspected vinyl asbesto pin to Hybrid only"*. Not open book: the job was dropped from open book
 * the same day (*"this job is way too complicated and not worth going discounting for"*), so nothing here is a cost.
 *
 * Each product carries ONE all-in sell rate per m² (supply, install, preparation, margin inside). That is the point of a
 * separate offer: the open-book chooser prices every product as board + underlay + ONE labour rate, which cannot price
 * a glued vinyl tile on a double skim next to a floating hybrid — their install and prep differ, and the single rate
 * either over-charges one or under-charges the other.
 *
 * Total = Σ (room m² × chosen rate, each room line rounded to the nearest dollar) + the fixed lines, EX GST, shown
 * "$x + GST". Pure: the page, the backbone (save, accept, read) and EmmA reach the same figure from the same inputs.
 */
import { z } from 'zod';
import { CustomerPricing, CustomerPricedLine } from './customer-pricing.js';
import { CustomerSelection } from './customer-offer.js';
import { RoomChoiceOffer } from './room-choice-offer.js';
import { wasteFor, wastePercent } from './waste.js';
import { ProposalSelection, UNDECIDED_COLOUR } from './base-pricing.js';
export { RoomChoiceOffer, RoomChoiceProduct, RoomChoiceRoom, RoomChoiceGroup, RoomChoiceFixedLine } from './room-choice-offer.js';
const r2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;
/** To the nearest dollar — a room line, a fixed line. */
export const wholeDollar = (n) => Math.round(r2(n));
/** The products a room may take. */
export function roomChoiceOptions(offer, roomId) { return offer.products.filter(p => p.rooms === null || p.rooms.includes(roomId)); }
/** The products every room of a group may take — what the group's one select offers. */
export function groupChoiceOptions(offer, group) { return offer.products.filter(p => group.rooms.every(id => p.rooms === null || p.rooms.includes(id))); }
/** The recommended selection — what the page opens on before anything was chosen. */
export function defaultRoomChoiceSelection(offer) {
    return CustomerSelection.parse({ rooms: Object.fromEntries(offer.rooms.map(r => [r.id, { product: offer.recommended[r.id], orientation: null }])) });
}
/** The product a room takes: the chosen one when it is offered there, else the recommended one. For DISPLAY; a save validates instead. */
export function roomChoiceProduct(offer, roomId, selection) {
    const options = roomChoiceOptions(offer, roomId);
    return options.find(p => p.id === selection?.rooms[roomId]?.product) ?? options.find(p => p.id === offer.recommended[roomId]) ?? options[0];
}
/** The selection a saved one becomes on this offer: every room filled, anything no longer offered back to the recommendation. */
export function effectiveRoomChoiceSelection(offer, selection) {
    return CustomerSelection.parse({ rooms: Object.fromEntries(offer.rooms.map(r => [r.id, { product: roomChoiceProduct(offer, r.id, selection).id, orientation: null }])) });
}
/** The group's product when every room in it takes the same one; null when the rooms are mixed. */
export function groupChoice(offer, group, selection) {
    const ids = new Set(group.rooms.map(id => roomChoiceProduct(offer, id, selection).id));
    return ids.size === 1 ? [...ids][0] : null;
}
/** One tap for a whole group: every room in it takes the product. Refused (unchanged) when a room may not take it. */
export function applyGroupChoice(offer, selection, groupId, productId) {
    const base = effectiveRoomChoiceSelection(offer, selection);
    const group = offer.groups.find(g => g.id === groupId);
    if (!group || !groupChoiceOptions(offer, group).some(p => p.id === productId))
        return base;
    return { ...base, rooms: { ...base.rooms, ...Object.fromEntries(group.rooms.map(id => [id, { product: productId, orientation: null }])) } };
}
export function applyRoomChoice(offer, selection, roomId, productId) {
    const base = effectiveRoomChoiceSelection(offer, selection);
    if (!roomChoiceOptions(offer, roomId).some(p => p.id === productId))
        return base;
    return { ...base, rooms: { ...base.rooms, [roomId]: { product: productId, orientation: null } } };
}
/**
 * What a save or an acceptance must refuse: a room not on the offer, a product not on it, a product in a room it is not
 * offered in (a pinned room with anything but its one product). The page never offers these; a stale tab or a hand-made
 * request can still send them, and silently substituting would accept a price nobody saw.
 */
export function roomChoiceSelectionErrors(offer, selection) {
    const errors = [];
    for (const [roomId, pick] of Object.entries(selection.rooms)) {
        const room = offer.rooms.find(r => r.id === roomId);
        if (!room) {
            errors.push(`Room ${roomId} is not on this offer`);
            continue;
        }
        const product = offer.products.find(p => p.id === pick.product);
        if (!product) {
            errors.push(`${pick.product} is not offered on this Proposal`);
            continue;
        }
        if (!roomChoiceOptions(offer, roomId).some(p => p.id === product.id))
            errors.push(room.pinned_reason ? `${room.name} takes ${roomChoiceOptions(offer, roomId)[0]?.name ?? 'one product'} only: ${room.pinned_reason}` : `${product.name} is not offered in ${room.name}`);
    }
    // A colour must be one the board offers; null clears the pick. The same check for the page's save and the acceptance.
    for (const [productId, colour] of Object.entries(selection.colours ?? {})) {
        const product = offer.products.find(p => p.id === productId);
        if (!product) {
            errors.push(`${productId} is not offered on this Proposal`);
            continue;
        }
        if (colour !== null && !(product.colours ?? []).some(c => c.id === colour))
            errors.push(`${colour} is not a colour of ${product.name}`);
    }
    return errors;
}
/** The m² a room is charged for: its measured m² plus the product's frozen cutting allowance (a treatment adds nothing). Unrounded:
 *  the dollar rounding happens once, on the amount, so an offer saved before the field prices to the cent as it did (Minto, $49,683). */
export function roomChoiceChargeableM2(room, product) { return room.area_m2 * (1 + (product.cutting_allowance ?? 0)); }
/** One room's sell line: chargeable m² × the product's rate per m² of board, to the nearest dollar. */
export function roomChoiceAmount(room, product) { return wholeDollar(roomChoiceChargeableM2(room, product) * product.rate_per_m2); }
/** The fixed lines a group's card lists: its own, in offer order. `budget` ones are the forward-looking worst cases, shown apart. */
export function roomChoiceGroupLines(offer, groupId, kind) {
    return offer.fixed_lines.filter(f => (f.group ?? null) === groupId && (f.kind ?? 'fixed') === kind);
}
/**
 * ONE STEP'S OWN TOTAL (incremental exposure, 26 Oct 2026): the group's rooms at the chosen products plus the fixed lines that
 * belong to it. A budget line is never in it — the customer reads the bundle first, then the known issue's worst case.
 */
export function roomChoiceGroupTotal(offer, group, selection) {
    const rooms = group.rooms.reduce((n, id) => { const room = offer.rooms.find(r => r.id === id); return room ? n + roomChoiceAmount(room, roomChoiceProduct(offer, id, selection)) : n; }, 0);
    return rooms + roomChoiceGroupLines(offer, group.id, 'fixed').reduce((n, f) => n + f.amount, 0);
}
const WORDS = {
    en: { rooms: (names) => names.join(', '), notice: 'Every amount is the installed price, excluding GST. Choose a floor for each area; the total updates as you choose.',
        cutting: (percent, measured) => `Includes ${percent}% for cutting and waste on ${measured.toFixed(2)} m² measured; the final charge is by actual measurement.`,
        onlyIfUsed: 'Items under "only if needed" are priced and agreed now and are charged only if we use them; you approve each one on the day.' },
    zh: { rooms: (names) => names.join('、'), notice: '所有金额均为含安装的价格，不含 GST。请为每个区域选择地板，合计会随选择即时更新。',
        cutting: (percent, measured) => `含 ${percent}% 裁切损耗（实测 ${measured.toFixed(2)} m²）；最终按实际用量结算。`,
        onlyIfUsed: '「仅在需要时」项目现已定价并商定，只有在实际使用时才收费；每一项都由您在当天确认。' },
};
/**
 * THE OFFER PRICED FOR ONE SELECTION — one line per product across the rooms that take it (its amount the sum of the
 * rooms' whole-dollar lines), then the fixed lines. No `open_book`, no `cost_items`: a sell copy like any standard
 * Proposal, with the offer frozen in `room_choice` so any other selection prices the same way.
 */
export function priceRoomChoice(offer, selection, lang = 'en') {
    const w = WORDS[lang];
    const lines = [];
    for (const product of offer.products) {
        const rooms = offer.rooms.filter(r => roomChoiceProduct(offer, r.id, selection).id === product.id);
        if (!rooms.length)
            continue;
        // The line's quantity is the CHARGEABLE m² (measured + the frozen allowance) so rate × quantity is the amount the customer reads;
        // the sentence names the measured figure and the percentage, the way the CRM's product lines do (waste.ts).
        const measured = r2(rooms.reduce((n, r) => n + r.area_m2, 0)), m2 = r2(rooms.reduce((n, r) => n + roomChoiceChargeableM2(r, product), 0)), amount = rooms.reduce((n, r) => n + roomChoiceAmount(r, product), 0);
        const allowance = product.cutting_allowance ?? 0;
        const note = [w.rooms(rooms.map(r => r.name)), ...(allowance > 0 ? [w.cutting(wastePercent(allowance), measured)] : [])].join(' · ').slice(0, 300);
        // The colour the customer picked for this board rides on its line (CRM, acceptance, Job); nothing picked, nothing said.
        const colour = (product.colours ?? []).find(c => c.id === selection?.colours?.[product.id])?.name ?? null;
        lines.push(CustomerPricedLine.parse({ selection_id: `product:${product.id}`, label: product.name, unit: 'm²', quantity: m2, unitSell: product.rate_per_m2, sell: amount, sellRange: null, kind: 'product', note, colour }));
    }
    // A grouped line carries its bundle's name as the priced line's `group`, the label the customer document already prints under a line.
    // A BUDGET is a variation (26 Oct 2026): priced and agreed now, approved on the day, charged only if used — never in the total. A
    // budget with no rate (a stain priced after the sample patch) is "rate to confirm".
    const variations = [];
    for (const f of offer.fixed_lines) {
        const group = (offer.groups.find(g => g.id === f.group)?.name ?? '').slice(0, 60);
        if ((f.kind ?? 'fixed') === 'budget') {
            variations.push(CustomerPricedLine.parse({ selection_id: `fixed:${f.id}`, label: f.label, group, unit: f.unit, quantity: f.quantity, unitSell: f.rate > 0 ? f.rate : null, sell: f.rate > 0 ? f.amount : null, sellRange: null, kind: 'variation', included_in_price: false, note: f.note }));
            continue;
        }
        lines.push(CustomerPricedLine.parse({ selection_id: `fixed:${f.id}`, label: f.label, group, unit: f.unit, quantity: f.quantity, unitSell: f.rate, sell: f.amount, sellRange: null, kind: 'service', note: f.note }));
    }
    const subtotal = r2(lines.reduce((n, l) => n + (l.sell ?? 0), 0)), gst = r2(subtotal * 0.1);
    return CustomerPricing.parse({ lines, optionGroups: [], subtotal, subtotalRange: null, gst, total: r2(subtotal + gst), totalRange: null, variations, notices: [w.notice, ...(variations.length ? [w.onlyIfUsed] : [])], open_book: null, offer: null, room_choice: offer });
}
export function roomChoiceSummary(offer, selection) {
    const steps = offer.groups.map(g => ({ label: g.name, amount: roomChoiceGroupTotal(offer, g, selection) }));
    const loose = roomChoiceGroupLines(offer, null, 'fixed');
    if (loose.length)
        steps.push(...loose.map(f => ({ label: f.label, amount: f.amount })));
    const price = priceRoomChoice(offer, selection).subtotal ?? 0;
    const provisional = offer.fixed_lines.filter(f => (f.kind ?? 'fixed') === 'budget').map(f => ({ label: f.label, amount: f.rate > 0 ? f.amount : null }));
    return { steps, price, provisional, closing: offer.closing ?? 'accept_on_page' };
}
/**
 * DOES THE CRM AGREE WITH THE PAGE? (23 Oct 2026, Minto P-1026-10296.) A release publishes the room choice, not the builder's
 * lines, so work priced only in the builder never reaches the customer: Minto's first floor ($12,593) sat in the CRM's
 * $57,686 while the page said $45,093. Both the release (backbone) and the Share step (CRM) ask this before a release; the
 * answer is null when they agree, or when the builder priced nothing (a room choice EmmA set up on her own).
 */
export function roomChoiceReleaseMismatch(offer, builderSubtotal) {
    if (builderSubtotal === null || builderSubtotal === undefined || !(builderSubtotal > 0))
        return null;
    const page = priceRoomChoice(offer, defaultRoomChoiceSelection(offer)).subtotal ?? 0;
    return Math.abs(page - builderSubtotal) < 0.5 ? null : { page, crm: r2(builderSubtotal) };
}
/**
 * "BACK TO OUR RECOMMENDATION" FOLLOWS THE RECOMMENDATION (23 Oct 2026, Minto). The page used to save every room's pick, so a reset
 * pinned that day's recommendation; when Artemis later moved the east block from hybrid to vinyl tile, the saved picks kept it on
 * hybrid and the page showed $58,684 against a released $64,831. A reset now saves no room picks — every room then takes whatever
 * the offer recommends, today and after the next change — and keeps the customer's other choices (colours, skirting, disposal).
 */
export function resetRoomChoiceSelection(selection) {
    return CustomerSelection.parse({ ...(selection ?? {}), rooms: {} });
}
/**
 * WHAT THE CUSTOMER'S SAVED CHOICE DOES TO THE PRICE. Release prices the page at the recommendation; the page and an acceptance
 * price it with the saved choice. Null when the saved choice prices the same as the recommendation; otherwise both figures and
 * the rooms whose floor differs, so staff see what the customer's page shows before they release or record an acceptance.
 */
export function roomChoiceSelectionDrift(offer, selection) {
    if (!selection || !Object.keys(selection.rooms).length)
        return null;
    const recommended = priceRoomChoice(offer, defaultRoomChoiceSelection(offer)).subtotal ?? 0, chosen = priceRoomChoice(offer, selection).subtotal ?? 0;
    const rooms = offer.rooms.filter(r => roomChoiceProduct(offer, r.id, selection).id !== roomChoiceProduct(offer, r.id, null).id).map(r => r.name);
    return Math.abs(chosen - recommended) < 0.5 && !rooms.length ? null : { recommended, chosen, rooms };
}
/**
 * HOW STAFF AND EMMA DESCRIBE THE OFFER — by room NAME (or id), the way Jacky says it, resolved against the bound
 * canvas plan when it is saved. A group may also be "every room of this finish" (`finish: 'Carpet Tile'`). A pin is a
 * group-level instruction ("East block → Hybrid only, because…") that becomes, on the stored offer, the other products'
 * room lists plus each room's `pinned_reason` — the stored shape is the plain one the page and the backbone check.
 */
export const RoomChoiceInput = z.object({
    /** Room names resolve on this floor only (the plan's floor name, e.g. "Ground floor"); null = every floor. */
    floor: z.string().trim().max(80).nullable().default(null),
    products: z.array(z.object({ id: z.string().trim().min(1).max(80), name: z.string().trim().min(1).max(200), intro: z.string().max(400).default(''), rate_per_m2: z.number().finite().nonnegative().max(1e6),
        /** Room names or ids this product may go in; null = any room that is not pinned to another product. */
        rooms: z.array(z.string().trim().min(1).max(120)).max(200).nullable().default(null),
        /** A board laid from stock: the saved offer freezes the cutting allowance the `artemis_services.waste` rule gives the area it is
         *  offered in (7% under 50 m², 5% from 50 m², straight lay), and the rate stays per m² of board. A treatment leaves this false. */
        board: z.boolean().default(false),
        /** The catalogue product this is: the save freezes its photo, spec and colours onto the offer (`profiles` on the build). */
        product_id: z.string().trim().max(160).nullable().default(null),
        /** The settled colour variant of that product, for the Job's lines and the PO office; null = not yet settled. */
        variant_id: z.string().trim().max(160).nullable().default(null) })).min(1).max(30),
    groups: z.array(z.object({ id: z.string().trim().min(1).max(60).optional(), name: z.string().trim().min(1).max(120),
        /** The step's own words under its name. */
        intro: z.string().trim().max(600).default(""),
        rooms: z.array(z.string().trim().min(1).max(120)).max(200).default([]),
        /** Every in-scope room on the floor with this canvas finish, as well as `rooms`. */
        finish: z.string().trim().max(60).nullable().default(null),
        pin: z.object({ product: z.string().trim().min(1).max(80), reason: z.string().trim().min(1).max(300) }).nullable().default(null) })).min(1).max(30),
    /** The recommended product, by group id / group name, or by room name / id for a single room. */
    recommended: z.record(z.string(), z.string()),
    /** Lines that do not move with the choices — and any included work outside the rooms (a whole other floor), so the page total is the CRM's. */
    /** How the Proposal closes: accept on the page (default), or call Jacky — no acceptance on the page, on the page and in the email. */
    closing: z.enum(['accept_on_page', 'call']).default('accept_on_page'),
    fixed_lines: z.array(z.object({ label: z.string().trim().min(1).max(200), quantity: z.number().finite().nonnegative().max(1e6), unit: z.string().trim().min(1).max(20), rate: z.number().finite().nonnegative().max(1e6),
        /** Printed under the line on the customer's page. */
        note: z.string().trim().max(300).default(''),
        /** The group (by name or id) whose card lists this line and whose total includes it; null = under every group ("Included whatever you choose"). */
        group: z.string().trim().max(120).nullable().default(null),
        /** 'budget': a forward-looking worst case, shown under its own heading, counted in the whole total and never in a bundle's. */
        kind: z.enum(['fixed', 'budget']).default('fixed') })).max(30).default([]),
});
const slug = (s) => s.toLowerCase().normalize('NFKD').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'group';
const key = (s) => s.trim().toLowerCase();
export function buildRoomChoiceOffer(plan, raw, waste, profiles) {
    const input = RoomChoiceInput.parse(raw);
    const errors = [];
    if (input.products.some(p => p.board) && !waste)
        errors.push('A board needs the cutting-waste rule (artemis_services.waste) to freeze its allowance');
    for (const p of input.products)
        if (p.product_id && !profiles?.[p.product_id])
            errors.push(`${p.name}: no catalogue product ${p.product_id} to take the profile from`);
    const floors = input.floor ? plan.filter(f => key(f.name) === key(input.floor)) : plan;
    if (input.floor && !floors.length)
        return { offer: null, errors: [`No floor called "${input.floor}" on the bound plan (${plan.map(f => f.name).join(', ') || 'no plan'})`] };
    const inScope = floors.flatMap(f => f.rooms.filter(r => !r.out_of_scope && !r.is_void && r.in_proposal !== false).map(r => ({ ...r, floor: f.name })));
    const resolve = (ref, where) => {
        const byId = inScope.find(r => r.id === ref);
        if (byId)
            return byId;
        const named = inScope.filter(r => key(r.name) === key(ref));
        if (named.length === 1)
            return named[0];
        errors.push(named.length ? `${where}: "${ref}" matches ${named.length} rooms — use the room id` : `${where}: no in-scope room called "${ref}"${input.floor ? ` on ${input.floor}` : ''}`);
        return null;
    };
    const groups = input.groups.map(g => {
        const rooms = [...g.rooms.map(ref => resolve(ref, `Group ${g.name}`)), ...(g.finish ? inScope.filter(r => key(r.finish ?? '') === key(g.finish)) : [])].filter((r) => !!r);
        if (g.finish && !inScope.some(r => key(r.finish ?? '') === key(g.finish)))
            errors.push(`Group ${g.name}: no in-scope room has the finish "${g.finish}"`);
        return { id: g.id ?? slug(g.name), name: g.name, intro: g.intro, rooms: [...new Map(rooms.map(r => [r.id, r])).values()], pin: g.pin };
    });
    if (new Set(groups.map(g => g.id)).size !== groups.length)
        errors.push('Two groups have the same id; give each group its own id');
    // A room belongs to one group: two groups choosing the same room would fight over it.
    const owner = new Map();
    for (const g of groups)
        for (const r of g.rooms) {
            const was = owner.get(r.id);
            if (was && was !== g.name)
                errors.push(`${r.name} is in both ${was} and ${g.name}`);
            owner.set(r.id, g.name);
        }
    const rooms = [...new Map(groups.flatMap(g => g.rooms).map(r => [r.id, r])).values()];
    const pinnedTo = new Map();
    for (const g of groups)
        if (g.pin) {
            if (!input.products.some(p => p.id === g.pin.product))
                errors.push(`Group ${g.name} is pinned to ${g.pin.product}, which is not offered`);
            for (const r of g.rooms)
                pinnedTo.set(r.id, g.pin);
        }
    const products = input.products.map(p => {
        const named = p.rooms === null ? null : p.rooms.map(ref => resolve(ref, p.name)).filter((r) => !!r).map(r => r.id);
        for (const id of named ?? [])
            if (!rooms.some(r => r.id === id))
                errors.push(`${p.name}: ${inScope.find(r => r.id === id)?.name ?? id} is not in any group`);
        // A pinned room takes its one product only, whatever the product's own list says.
        const allowed = (named ?? rooms.map(r => r.id)).filter(id => { const pin = pinnedTo.get(id); return !pin || pin.product === p.id; });
        // The allowance is read on the area the board is OFFERED in and frozen: a board later picked for one small room still carries
        // the tier of its offered area, which is the figure Artemis quoted on (straight lay; a patterned lay is not a room-choice case).
        const offeredM2 = Math.round(rooms.filter(r => allowed.includes(r.id)).reduce((n, r) => n + r.area_m2, 0) * 100) / 100;
        const cutting_allowance = p.board && waste ? wasteFor(offeredM2, 'straight', waste) : 0;
        const profile = p.product_id ? profiles?.[p.product_id] : undefined;
        return { id: p.id, name: p.name, intro: p.intro, rate_per_m2: p.rate_per_m2, rooms: p.rooms === null && ![...pinnedTo.values()].length ? null : allowed, cutting_allowance,
            product_id: p.product_id ?? null, hero: profile?.hero ?? null, specs: (profile?.specs ?? []).slice(0, 20), colours: (profile?.colours ?? []).slice(0, 60), variant_id: p.product_id ? p.variant_id ?? null : null };
    });
    const recommended = {};
    for (const [ref, productId] of Object.entries(input.recommended)) {
        if (!products.some(p => p.id === productId)) {
            errors.push(`Recommended ${ref}: ${productId} is not offered`);
            continue;
        }
        const group = groups.find(g => g.id === ref || key(g.name) === key(ref));
        if (group) {
            for (const r of group.rooms)
                recommended[r.id] = productId;
            continue;
        }
        const room = resolve(ref, 'Recommended');
        if (room)
            recommended[room.id] = productId;
    }
    // A room with one product needs no recommendation from anyone.
    for (const r of rooms)
        if (!recommended[r.id]) {
            const only = products.filter(p => p.rooms === null || p.rooms.includes(r.id));
            if (only.length === 1)
                recommended[r.id] = only[0].id;
        }
    if (errors.length)
        return { offer: null, errors };
    const fixed = input.fixed_lines.map((f, i) => {
        const group = f.group === null ? null : (groups.find(g => g.id === f.group || key(g.name) === key(f.group))?.id ?? null);
        if (f.group !== null && !group)
            errors.push(`${f.label}: no group called "${f.group}" on the offer`);
        return { id: `${i + 1}-${slug(f.label).slice(0, 40)}`, label: f.label, quantity: f.quantity, unit: f.unit, rate: f.rate, amount: wholeDollar(f.quantity * f.rate), note: f.note, group, kind: f.kind };
    });
    if (errors.length)
        return { offer: null, errors };
    const parsed = RoomChoiceOffer.safeParse({ products, rooms: rooms.map(r => ({ id: r.id, name: r.name, floor: r.floor, area_m2: r.area_m2, polygon: r.shape.map(p => ({ x: p.x, y: p.y })), pinned_reason: pinnedTo.get(r.id)?.reason ?? null })), groups: groups.map(g => ({ id: g.id, name: g.name, intro: g.intro, rooms: g.rooms.map(r => r.id) })), recommended, fixed_lines: fixed, closing: input.closing });
    if (!parsed.success)
        return { offer: null, errors: parsed.error.issues.map(i => i.message) };
    return { offer: parsed.data, errors: [] };
}
/**
 * THE FLOORS TO ORDER from a room choice (26 Oct 2026), in the shape the PO plan and the material snapshot already read
 * (`ProposalSelection`): one per catalogue-bound product the customer ends up with under `selection` (the recommendation when
 * null), its quantity the MEASURED m² of its rooms — the cutting allowance is a selling figure, and the purchase overage is the
 * PO office's own rule. An offer product with no `product_id` orders nothing, so the row that would have been it is not there.
 */
export function roomChoiceFloorSelections(offer, selection) {
    const out = [];
    for (const product of offer.products) {
        if (!product.product_id)
            continue;
        const rooms = offer.rooms.filter(r => roomChoiceProduct(offer, r.id, selection).id === product.id);
        if (!rooms.length)
            continue;
        const quantity = r2(rooms.reduce((n, r) => n + r.area_m2, 0));
        if (!(quantity > 0))
            continue;
        // The colour: the customer's pick on the page (`selection.colours[product.id]`, one of the frozen colours) by name, else undecided.
        const pick = selection?.colours?.[product.id] ?? null, colour = pick ? product.colours.find(c => c.id === pick)?.name ?? UNDECIDED_COLOUR : UNDECIDED_COLOUR;
        out.push(ProposalSelection.parse({ id: `room-choice:${product.id}`, productId: product.product_id, variantId: product.variant_id ?? undefined, mode: 'supply_install', quantity, area: quantity, rooms: rooms.map(r => r.name), colour, role: 'recommended' }));
    }
    return out;
}
//# sourceMappingURL=room-choice.js.map